import { TimeZoneService } from './../../modules/shared/services/timezone.service';
import { CustomField } from './../../modules/shared/model/customField.model';
import { Injectable } from '@angular/core';

// Models
import { Account } from "../models/account.model";
import { AccountAdd, Address } from '../models/accountAdd.model';

// Pipes
import { DatePipe } from '@angular/common';

// Constants
import {
    AccountNameFields, DropdownFields, ContactEmailFields, ContactPhoneFields, ContactSocialFields, CustomAttributeFields,
    AddressFields, EmailFields, PhoneFields, SocialFields
} from '../accounts.constant';
import {
    EMAIL_TYPE_IDS as EmailTypes, PHONE_TYPE_IDS as PhoneTypes, SOCIAL_PROFILE_TYPE_IDS as SocialProfileTypes,
    CUSTOMFIELD_TYPE_IDS as CustomFieldTypes
}
    from 'src/app/modules/shared/constants/typeId';
import { Fields, SectionN } from 'src/app/modules/shared/models/column-settings.model';
import { FormControlList } from 'src/app/modules/shared/constants/formcontrol-list.constants';

@Injectable({
    providedIn: 'root'
})

export class AccountHelper {
    constructor(
        public datePipe: DatePipe,
        private timeZoneService: TimeZoneService
    ) { }

    processAddressPayloadOnAdd(addressList: any[]): any[] {
        const addresses: Address[] = [];

        for (const { address } of addressList) {
            const { addressTypeId, addressClassId,addressName, streetAddress1, streetAddress2, countryId, cityId, stateId, zipCode,
                attnContactId, statusId, isDefault, latitude, longitude } = address;

            const attnId = attnContactId;
            const isActive = statusId === 1 ? true : false;

            const addressObj = {
                addressTypeId,
                addressClassId,
                streetAddress1,
                streetAddress2,
                addressName,
                countryId,
                cityId,
                stateId,
                zipCode,
                attnId,
                isActive,
                isDefault,
                latitude,
                longitude
            };

            addresses.push(addressObj);
        };

        return addresses;
    }

    processFormValues(formValues: AccountAdd): AccountAdd {
        let data: AccountAdd = {} as AccountAdd;

        for (const key in formValues) {
            let keyData = formValues[key];

            if (keyData) {
                if (DropdownFields.includes(key)) {
                    if (Array.isArray(keyData)) {
                        keyData = keyData.map(el => el.id);
                    } else {
                        keyData = keyData?.id||'';
                    }
                } else if (AccountNameFields.includes(key)) {
                    keyData = keyData?.trim() || '';
                }

                data[key] = keyData;
            }
        }

        return data;
    }

    processFormData(formValue: AccountAdd): FormData {
        const formData = new FormData();

        for (let key in formValue) {
            if ((EmailFields.includes(key) && key.startsWith('contact')) || (PhoneFields.includes(key) && key.startsWith('contact')) ||
                (SocialFields.includes(key) && key.startsWith('contact'))) {
                continue;
            }

            const keyData = formValue[key];

            if (Array.isArray(keyData)) {
                const length = keyData.length;

                for (let i = 0; i < length; i++) {
                    const keyDataValue = keyData[i];

                    if (typeof keyDataValue === 'object') {
                        switch (key) {
                            case 'contactEmails': {
                                if (keyDataValue['emailAddress'].length) {
                                    for (const field of ContactEmailFields) {
                                        const fieldValue = keyDataValue[field];
                                        formData.append(`${key}[${i}].${field}`, fieldValue);
                                    }
                                }
                                break;
                            }

                            case 'contactPhones': {
                                if (keyDataValue['phoneNumber'].length) {
                                    for (const field of ContactPhoneFields) {
                                        const fieldValue = keyDataValue[field];
                                        formData.append(`${key}[${i}].${field}`, fieldValue);
                                    }
                                }
                                break;
                            }

                            case 'contactSocialProfiles': {
                                if (keyDataValue['socialProfileAddress'].length) {
                                    for (const field of ContactSocialFields) {
                                        const fieldValue = keyDataValue[field];
                                        formData.append(`${key}[${i}].${field}`, fieldValue);
                                    }
                                }
                                break;
                            }

                            case 'contactProfileImages': {
                                formData.append(`${key}`, keyDataValue);
                                break;
                            }

                            case 'customAttributeValues': {
                                for (const field of CustomAttributeFields) {
                                    const fieldValue = keyDataValue[field];

                                    if (fieldValue) {
                                        if (field === 'values') {
                                            for (let j = 0; j < fieldValue.length; j++) {
                                                formData.append(`${key}[${i}].${field}`, fieldValue[j]);
                                            }
                                        } else if (field === 'attachments') {
                                            for (let j = 0; j < fieldValue.length; j++) {
                                                formData.append(`${key}[${i}].${field}[${j}].name`, fieldValue[j].name);
                                                formData.append(`${key}[${i}].${field}[${j}].extension`, fieldValue[j].extension);
                                                formData.append(`${key}[${i}].${field}[${j}].content`, fieldValue[j].content);
                                                formData.append(`${key}[${i}].${field}[${j}].sizeinbytes`, fieldValue[j].size);
                                            }
                                        } else {
                                            formData.append(`${key}[${i}].${field}`, fieldValue);
                                        }
                                    }
                                }
                                break;
                            }

                            case 'addresses': {
                                for (const field of AddressFields) {
                                    const fieldValue = keyDataValue[field];

                                    if (fieldValue) {
                                        formData.append(`${key}[${i}].${field}`, fieldValue);
                                    }
                                }
                                break;
                            }

                            case 'tags': {
                                formData.append(`${key}[${i}].${'name'}`, keyDataValue?.name);
                                formData.append(`${key}[${i}].${'id'}`, keyDataValue?.id);
                                formData.append(`${key}[${i}].${'color'}`, keyDataValue?.color);
                                break;
                            }
                        }
                    } else {
                        formData.append(key, keyDataValue);
                    }
                }
            } else {
                formData.append(key, keyData);
            }
        }
        return formData;
    }

    formatCustomFieldData(customFieldData: any, fieldDetails: CustomField): any {
        customFieldData = Array.isArray(customFieldData) ? customFieldData : [customFieldData];

        customFieldData = customFieldData.map(value => {
            if (value?.constructor?.name === 'Object') {
                return value.name;
            }

            if(fieldDetails?.formControl?.type==FormControlList.DatePicker && value){
                return this.timeZoneService._getCustomDateString(value);
            }else if(fieldDetails?.formControl?.type==FormControlList.DateTimePicker && value){
                return this.timeZoneService._getCustomDateTimeString(value);
            }

            return value;
        });

        return customFieldData;
    }

    processAccountNames(accountTypes): string {
        let names: string = "";
        accountTypes.forEach(type => { names = names + ' ' + type.type.name });
        return names;
    }

    getCustomFieldValue(customFieldName, customAttributeValues): string {
        let value: string = '';

        if (customAttributeValues?.length) {

            const matchedValue = customAttributeValues.find(customAttributeValue => customAttributeValue.customAttribute.fieldName ==
                customFieldName.name);

            if (matchedValue) {
                value = matchedValue?.value;
                switch (matchedValue.customAttribute.formControlId) {
                    // DropdownList
                    case 1: {
                        value = matchedValue.value ? JSON.parse(matchedValue.value).name : null;
                        break;
                    }

                    // Checkbox
                    case 2: {
                        value = matchedValue.value ? JSON.parse(matchedValue.value).join() : null;
                        break;
                    }

                    // Radio Button
                    case 3: {
                        value = matchedValue.value ? matchedValue.value : null;
                        break;
                    }

                    // Toggle Button
                    case 4: {
                        value = matchedValue.value;
                        break;
                    }

                    // Rating
                    case 7: {
                        value = matchedValue.value;
                        break;
                    }
                    // Number
                    case CustomFieldTypes.NUMBER: {
                        value = matchedValue.value;
                        break;
                    }

                    // Decimal
                    case CustomFieldTypes.DECIMAL: {
                        value = matchedValue.value;
                        break;
                    }

                    // Input
                    case 8: {
                        value = matchedValue.value;
                        break;
                    }

                    // Datepicker
                    case CustomFieldTypes.DATE_PICKER: {
                        value = matchedValue.value ? this.datePipe.transform(matchedValue.value, 'dd/MM/yy') : '';
                        break;
                    }

                    // DateTimePicker
                    case CustomFieldTypes.DATETIMEPICKER: {
                        value = matchedValue.value ? this.timeZoneService.formateToUserDate(matchedValue.value,"MM/DD/YYYY | h:mm a",false) : '';
                        break;
                    }

                    // Multiple DropDownList
                    case 11: {
                        const arrVal = matchedValue.value ? JSON.parse(matchedValue.value) : [];

                        if (arrVal.length) {
                            value = arrVal.map(val => val.name).join(', ')
                        } else {
                            value = '';
                        }

                        break;
                    }
                }
            }
        }

        return value;
    }

    removeCommas(account: Account, key: string): string {
        let keyData: string = account[key];

        if (keyData) {
            keyData = keyData?.trim();
            keyData = keyData?.endsWith(',') ? keyData?.substring(0, keyData.length - 1) : keyData;
        }

        return keyData;
    }

    getContactDetails(accountData) {
        return accountData?.accountContacts?.find(contact => contact?.isPrimaryContact)?.contact;
    }

    getContactEmails(contactData) {
        let workEmailAddress, homeEmailAddress, ce1EmailAddress, ce2EmailAddress;

        contactData?.contactEmails?.forEach(emailData => {
            const { emailAddress, emailTypeId } = emailData?.email;

            switch (emailTypeId) {
                case EmailTypes.WORK: {
                    workEmailAddress = workEmailAddress ? workEmailAddress : emailAddress;
                    break;
                }
                case EmailTypes.HOME: {
                    homeEmailAddress = homeEmailAddress ? homeEmailAddress : emailAddress;
                    break;
                }
                case EmailTypes.CE1: {
                    ce1EmailAddress = ce1EmailAddress ? ce1EmailAddress : emailAddress;
                    break;
                }
                case EmailTypes.CE2: {
                    ce2EmailAddress = ce2EmailAddress ? ce2EmailAddress : emailAddress;
                    break;
                }
            }
        });

        return { workEmailAddress, homeEmailAddress, ce1EmailAddress, ce2EmailAddress };
    }

    getContactPhones(contactData) {
        let mobilePhone: any = {}, homePhone: any = {}, workPhone: any = {}, cp1Phone: any = {}, cp2Phone: any = {};

        contactData?.contactPhones?.forEach(phoneData => {
            const { number, phoneTypeId, code } = phoneData?.phone;

            switch (phoneTypeId) {
                case PhoneTypes.MOBILE: {
                    mobilePhone = mobilePhone?.number ? mobilePhone : { number, countryCode: code };
                    break;
                }
                case PhoneTypes.HOME: {
                    homePhone = homePhone?.number ? homePhone : { number, countryCode: code };
                    break;
                }
                case PhoneTypes.WORK: {
                    workPhone = workPhone?.number ? workPhone : { number, countryCode: code };
                    break;
                }
                case PhoneTypes.CP1: {
                    cp1Phone = cp1Phone?.number ? cp1Phone : { number, countryCode: code };
                    break;
                }
                case PhoneTypes.CP2: {
                    cp2Phone = cp2Phone?.number ? cp2Phone : { number, countryCode: code };
                    break;
                }
            }
        });

        return { mobilePhone, homePhone, workPhone, cp1Phone, cp2Phone };
    }

    getContactSocialProfiles(contactData) {
        let contactWebsite, contactTwitter, contactFacebook, contactLinkedin, contactInstagram;

        contactData?.contactSocialProfiles?.forEach(socialData => {
            const { name, socialProfileTypeId } = socialData?.socialProfile;

            switch (socialProfileTypeId) {
                case SocialProfileTypes.WEBSITE: {
                    contactWebsite = name;
                    break;
                }
                case SocialProfileTypes.FACEBOOK: {
                    contactFacebook = name;
                    break;
                }
                case SocialProfileTypes.TWITTER: {
                    contactTwitter = name;
                    break;
                }
                case SocialProfileTypes.LINKEDIN: {
                    contactLinkedin = name;
                    break;
                }
                case SocialProfileTypes.INSTAGRAM: {
                    contactInstagram = name;
                    break;
                }
            }
        });

        return { contactWebsite, contactTwitter, contactFacebook, contactLinkedin, contactInstagram };
    }

    getContactProfileImages(contactData) {
        let profileImages = contactData?.contactProfileImages?.filter(el => el.attachment);

        profileImages = profileImages?.map(el => {
            const { attachment: { name, downloadLink, sizeInBytes } } = el;
            return {
                name,
                downloadLink,
                sizeInKb: (sizeInBytes / 1024).toFixed(2)
            }
        }) || [];

        return profileImages;
    }

    getAccountSocialProfiles(accountData) {
        let { accountSocials } = accountData;
        let accountWebsite, accountTwitter, accountFacebook, accountLinkedin, accountInstagram;

        accountSocials = accountSocials?.map(social => social?.socialProfile);

        accountSocials?.forEach(profile => {
            if (profile) {
                const { name, socialProfileTypeId } = profile;

                switch (socialProfileTypeId) {
                    case SocialProfileTypes.WEBSITE: {
                        accountWebsite = name;
                        break;
                    };
                    case SocialProfileTypes.TWITTER: {
                        accountTwitter = name;
                        break;
                    };
                    case SocialProfileTypes.FACEBOOK: {
                        accountFacebook = name;
                        break;
                    };
                    case SocialProfileTypes.LINKEDIN: {
                        accountLinkedin = name;
                        break;
                    };
                    case SocialProfileTypes.INSTAGRAM: {
                        accountInstagram = name;
                        break;
                    };
                }
            };
        });

        return { accountWebsite, accountTwitter, accountFacebook, accountLinkedin, accountInstagram };
    }

    processAccountDetailsData(accountData) {
        const contactData = this.getContactDetails(accountData);
        const { workEmailAddress, homeEmailAddress, ce1EmailAddress, ce2EmailAddress } = this.getContactEmails(contactData);
        accountData.contactSequenceNumber = contactData?.sequenceNumber;
        accountData.contactemailwork = workEmailAddress;
        accountData.contactemailhome = homeEmailAddress;
        accountData.contactemailce1 = ce1EmailAddress;
        accountData.contactemailce2 = ce2EmailAddress;

        const { mobilePhone, homePhone, workPhone, cp1Phone, cp2Phone } = this.getContactPhones(contactData);

        accountData.contactphonemobile = mobilePhone?.number;
        accountData.contactphonehome = homePhone?.number;
        accountData.contactphonework = workPhone?.number;
        accountData.contactphonecp1 = cp1Phone?.number;
        accountData.contactphonecp2 = cp2Phone?.number;

        accountData.contactphonemobileCode = mobilePhone?.countryCode;
        accountData.contactphonehomeCode = homePhone?.countryCode;
        accountData.contactphoneworkCode = workPhone?.countryCode;
        accountData.contactphonecp1Code = cp1Phone?.countryCode;
        accountData.contactphonecp2Code = cp2Phone?.countryCode;

        const { contactWebsite, contactTwitter, contactFacebook, contactLinkedin, contactInstagram } =
            this.getContactSocialProfiles(contactData);

        accountData.contactsocialwebsite = contactWebsite;
        accountData.contactsocialtwitter = contactTwitter;
        accountData.contactsocialfacebook = contactFacebook;
        accountData.contactsociallinkedin = contactLinkedin;
        accountData.contactsocialinstagram = contactInstagram;

        accountData.contact_roles = contactData?.contactRoles?.map(role => role.roleName)?.join(', ');

        const contactImages = this.getContactProfileImages(contactData);
        accountData.image = contactImages?.map(role => role.name)?.join(',\n')||"";

        accountData.customAttributeValues.filter(field=> field?.statusId==1).forEach(el => {
            let value: any = '';

            if(el.customAttribute.statusId!=3){
                switch (el.customAttribute.formControlId) {
                    case CustomFieldTypes.DROPDOWN:
                        try {
                            value = el.value ? JSON.parse(el.value)?.name : null;
                        } catch (e) {
                            value = null;
                        }
                        break;
                    case CustomFieldTypes.CHECKBOX:
                        try {
                            value = el.value ? JSON.parse(el.value)?.join(', ') : null;
                        } catch (e) {
                            value = null;
                        }
                        break;
                    case CustomFieldTypes.RADIO_BUTTON:
                        value = el.value ? el.value : null;
                        break;
                    case CustomFieldTypes.TOOGLE_BUTTON:
                        value = el.value;
                        break;
                    case CustomFieldTypes.RATING:
                        value = el.value;
                        break;
                    case CustomFieldTypes.INPUT:
                    case CustomFieldTypes.NUMBER:
                    case CustomFieldTypes.DECIMAL:
                    case CustomFieldTypes.PERCENTAGE:
                    case CustomFieldTypes.CURRENCY:
                    case CustomFieldTypes.MULTI_LINE:
                    case CustomFieldTypes.EMAIL:
                    case CustomFieldTypes.PHONE:
                    case CustomFieldTypes.URL:
                    case CustomFieldTypes.AUTONUMBER:
                    case CustomFieldTypes.FORMULA:
                    case CustomFieldTypes.ENCRYPTEDTEXT:
                    case CustomFieldTypes.TIME:
                    case CustomFieldTypes.IPADDRESS:
                    case CustomFieldTypes.RICHTEXT:
                    case CustomFieldTypes.BARCODE:
                        value = el.value;
                        break;
                    case CustomFieldTypes.USERLOOKUP:
                        value = el?.userLookupValue||'';
                        break;
                    case CustomFieldTypes.DATE_PICKER:
                        value = el.value ? this.datePipe.transform(el.value, 'MM/dd/yyyy') : '';
                        break;
                    case CustomFieldTypes.DATETIMEPICKER:
                        value = el.value ? this.timeZoneService.formateToUserDate(el.value,'MM/DD/YYYY | h:mm a',false) : '';
                        break;
                    case CustomFieldTypes.MULTISELECT:
                        const arrVal = el.value ? JSON.parse(el.value) : [];
                        if (arrVal.length) {
                            value = arrVal.map(val => val.name)?.join(', ')
                        } else {
                            value = '';
                        }
                        break;
                    default:
                        let files: any[] = [];
                        try {
                            files = (typeof el.value === 'string' && el.value.length) ? JSON.parse(el.value) : el.value;
                        } catch (error) {
                            files = [];
                        }

                        if (files?.length) {
                            value = files.filter(val => val?.name).map(val => val.name).join(',\n');
                        } else {
                            value = '';
                        }
                    break;
                }

                accountData[el.customAttribute.fieldName] = value;
            }

        });

        const { accountEmails, accountPhones } = accountData;

        accountData.work = accountEmails?.length ? accountEmails[0]?.email?.emailAddress : '';
        accountData.workphone = accountPhones?.length ? accountPhones[0]?.phone?.number : '';
        accountData.workphoneCountry = accountData.workphone ? accountPhones[0]?.phone?.country : '';
        accountData.workphoneCode = accountData.workphone ? accountPhones[0]?.phone?.code : '';

        const { accountWebsite, accountTwitter, accountFacebook, accountLinkedin, accountInstagram } =
            this.getAccountSocialProfiles(accountData);

        accountData.website = accountWebsite;
        accountData.twitter = accountTwitter;
        accountData.facebook = accountFacebook;
        accountData.linkedin = accountLinkedin;
        accountData.instagram = accountInstagram;

        accountData['address'] = (accountData['city'] ? accountData['city'] + ', ' : '')
            + (accountData['state'] ? accountData['state'] + ', ' : '')
            + (accountData['country'] ? accountData['country'] + ', ' : '')
            + (accountData['zipcode'] ? accountData['zipcode'] : '');

        // Format Account Types
        accountData.accountTypesName = this.removeCommas(accountData, 'accountTypesName');

        // Format Job Function
        // accountData.jobFunction = accountData?.jobFunction;
        accountData.activeInactiveStatus = accountData?.status?.name||"";

        const data: Account = {
            ...accountData,
        };

        return data;
    }

    processViewData(accountData) {
        if (!accountData) {
            return {};
        };

        const contactData = this.getContactDetails(accountData);
        const { workEmailAddress, homeEmailAddress, ce1EmailAddress, ce2EmailAddress } = this.getContactEmails(contactData);
        const { mobilePhone, homePhone, workPhone, cp1Phone, cp2Phone } = this.getContactPhones(contactData);
        const { contactWebsite, contactTwitter, contactFacebook, contactLinkedin, contactInstagram } =
            this.getContactSocialProfiles(contactData);
        const profileImages = this.getContactProfileImages(contactData);
        const { accountWebsite, accountTwitter, accountFacebook, accountLinkedin, accountInstagram } =
            this.getAccountSocialProfiles(accountData);

        const { businessName,sequenceNumber, status, mainCustomer, parentAccountSequenceNumber, ownerName, accountTypes, accountNumber, timezoneName, currencyUnit, doingBusinessAs, taxId,
            employeesRangeName, annualRevenueName, ownershipName, classificatioName, foundedYear, industryName, accountEmails, jobFunction,
            accountPhones, sicCode, naicsCode,
            sourceName, ratingName, description, createdByUserName, createdDate, updatedByUserName, updatedDate, currentStage, previousStage, stageChangedBy, stageChangedDate } = accountData;

        const viewAccountData = {
            contactSequenceNumber: contactData?.sequenceNumber,
            contactSalutation: contactData?.salutation?.name,
            contactFirstName: contactData?.firstName,
            contactMiddleName: contactData?.middleName,
            contactLastName: contactData?.lastName,
            workEmailAddress,
            homeEmailAddress,
            ce1EmailAddress,
            ce2EmailAddress,
            mobilePhone,
            homePhone,
            workPhone,
            cp1Phone,
            cp2Phone,
            contactWebsite,
            contactTwitter,
            contactFacebook,
            contactLinkedin,
            contactInstagram,
            profileImages,
            jobFunctions: contactData?.contactRoles?.map(role => role.roleName)?.join(', '),
            jobFunction,
            businessName,
            parentAccount: (typeof mainCustomer === 'string') ? mainCustomer : mainCustomer?.name,
            parentAccountSequenceNumber,
            ownerName,
            accountTypes: accountTypes?.map(el => el.type?.name)?.join(', '),
            sequenceNumber,
            accountNumber,
            timezoneName,
            currency: currencyUnit ? `${currencyUnit?.name} - ${currencyUnit?.code}` : null,
            doingBusinessAs,
            taxId,
            employeesRangeName,
            annualRevenueName,
            ownershipName,
            stage: classificatioName,
            foundedYear,
            industryName,
            accountEmail: accountEmails?.length ? accountEmails[0]?.email?.emailAddress : '',
            accountPhone: accountPhones?.length ? accountPhones[0]?.phone?.number : '',
            accountPhoneCountry: accountPhones?.length ? accountPhones[0]?.phone?.country : '',
            accountPhoneCode: accountPhones?.length ? accountPhones[0]?.phone?.code : '',
            accountWebsite,
            accountTwitter,
            accountFacebook,
            accountLinkedin,
            accountInstagram,
            sicCode,
            naicsCode,
            sourceName,
            ratingName,
            description,
            tags: accountData?.tags,
            createdByUserName,
            createdDate,
            updatedByUserName,
            updatedDate,
            currentStage,
            previousStage,
            stageChangedBy,
            stageChangedDate,
            status
        };
        return viewAccountData;
    }

    getSectionWiseFlatCols(section: SectionN[]): Fields[] {
        let fields = [];
        section.forEach(element => {
            element?.fields.forEach((field) => {
                if (field?.childFields.length > 0) {
                    fields = [...fields, ...field?.childFields]
                }else{
                    fields.push(field);
                }
            });
        });
        return fields;
    }

    processFieldName(fieldName: string): string {
        // Removes all parentheses from field name
        fieldName = fieldName.split('(')[0];

        // Removes all dots from field name
        fieldName = fieldName.split('.')[0];

        // Removes all slashes from field name
        fieldName = fieldName.split('/')[0];

        // Removes all spaces from field name
        fieldName = fieldName.split(' ').join('');

        return fieldName;
    }
}
